import { useState, useRef, useEffect } from "react";
import {
  Box,
  Stack,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  CircularProgress,
  LinearProgress,
  Chip,
} from "@pankod/refine-mui";
import { allLangs } from "locales/config";
import useLocales from "locales/useLocales";
import Image from "components/Image";
import axios from "utils/axios";
import { toast } from "react-toastify";
import TranslateIcon from "@mui/icons-material/Translate";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ErrorIcon from "@mui/icons-material/Error";

// Map frontend locale values to backend locale codes.
function toBackendLocale(value) {
  if (value.includes("ru")) return "ru-RU";
  return value.substring(0, 2);
}

// Check if a localization exists and is connected to the IT entry.
function isLocaleConnected(localizations, targetLocale) {
  if (!localizations || localizations.length === 0) return false;
  const backendLocale = toBackendLocale(targetLocale);
  // Exact match or base-language match.
  return localizations.some(
    (l) =>
      l.locale === backendLocale ||
      (l.locale && l.locale.substring(0, 2) === backendLocale.substring(0, 2)),
  );
}

export default function LocalizationColumn({ row, contentType, onTranslated }) {
  const { currentLang } = useLocales();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedLocale, setSelectedLocale] = useState(null);
  const [translating, setTranslating] = useState(false);
  const [jobStatus, setJobStatus] = useState(null);
  const pollRef = useRef(null);

  const localizations = row.localizations || [];

  useEffect(() => {
    return () => {
      if (pollRef.current) clearTimeout(pollRef.current);
    };
  }, []);

  const pollStatus = async (jobId) => {
    try {
      const { data: status } = await axios.get(
        `/api/translate-all-status?jobId=${jobId}&_t=${Date.now()}`,
      );
      setJobStatus(status);

      if (status.status === "running") {
        pollRef.current = setTimeout(() => pollStatus(jobId), 5000);
        return;
      }

      setTranslating(false);

      if (status.status === "done") {
        toast.success(
          `Translation complete: ${status.translated} translated, ${status.skipped} skipped, ${status.errors} errors`,
        );
        if (onTranslated) onTranslated();
      } else {
        toast.error("Translation failed: " + (status.error || "unknown error"));
      }
    } catch (error) {
      setTranslating(false);
      toast.error(
        "Translation status check failed: " +
          (error.message || "unknown error"),
      );
    }
  };

  const handleFlagClick = (lang) => {
    setSelectedLocale(lang);
    setJobStatus(null);
    setDialogOpen(true);
  };

  const handleConfirm = async () => {
    if (!selectedLocale) return;
    setTranslating(true);
    setJobStatus(null);
    try {
      const backendLocale = toBackendLocale(selectedLocale.value);
      const { data } = await axios.post("/api/translate-single", {
        contentType,
        entryId: row.id,
        targetLocale: backendLocale,
      });
      pollStatus(data.jobId);
    } catch (error) {
      setTranslating(false);
      toast.error(
        "Translation failed: " +
          (error.response?.data?.message || error.message),
      );
    }
  };

  const handleCancel = () => {
    if (translating) return;
    setDialogOpen(false);
    setSelectedLocale(null);
    setJobStatus(null);
  };

  const handleDone = () => {
    setDialogOpen(false);
    setSelectedLocale(null);
    setJobStatus(null);
  };

  return (
    <>
      <Stack direction="row" spacing={0.5} alignItems="center">
        {allLangs
          .filter(
            (lang) => lang.value !== "it" && lang.value !== currentLang?.value,
          )
          .map((lang) => {
            const connected = isLocaleConnected(localizations, lang.value);
            return (
              <Tooltip
                key={lang.value}
                title={
                  connected
                    ? `${lang.label} — connected (click to re-translate)`
                    : `${lang.label} — missing/faulty (click to translate)`
                }
              >
                <Box
                  onClick={() => handleFlagClick(lang)}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    cursor: "pointer",
                    gap: 0.25,
                    padding: "2px 4px",
                    borderRadius: 1,
                    "&:hover": {
                      bgcolor: "action.hover",
                    },
                  }}
                >
                  <Image
                    disabledEffect
                    src={lang.icon}
                    alt={lang.label}
                    sx={{ width: 20, height: 14, borderRadius: 0.5 }}
                  />
                  <Typography
                    component="span"
                    sx={{ fontSize: "0.85rem", lineHeight: 1 }}
                  >
                    {connected ? "✅" : "❌"}
                  </Typography>
                </Box>
              </Tooltip>
            );
          })}
      </Stack>

      <Dialog open={dialogOpen} onClose={handleCancel} maxWidth="xs" fullWidth>
        <DialogTitle>
          <Stack direction="row" alignItems="center" spacing={1}>
            <TranslateIcon color="primary" />
            <Typography variant="h6">
              {translating
                ? jobStatus?.phase === "writing"
                  ? "Writing to database..."
                  : `Translating to ${selectedLocale?.label}...`
                : jobStatus?.status === "done"
                  ? "Translation Complete"
                  : jobStatus?.status === "failed"
                    ? "Translation Failed"
                    : selectedLocale
                      ? `Translate to ${selectedLocale.label}`
                      : "Translate"}
            </Typography>
          </Stack>
        </DialogTitle>
        <DialogContent>
          {/* Confirmation prompt (before translation starts) */}
          {!translating && !jobStatus && selectedLocale && (
            <Typography variant="body2">
              {isLocaleConnected(localizations, selectedLocale.value)
                ? `This localization already exists and is connected. Do you want to re-translate "${row.title || row.id}" from Italian to ${selectedLocale.label}?`
                : `This localization is missing or faulty. Do you want to translate "${row.title || row.id}" from Italian to ${selectedLocale.label} and connect it?`}
            </Typography>
          )}

          {/* Progress display (while translating) */}
          {translating && jobStatus && (
            <Box sx={{ mt: 1 }}>
              {jobStatus.phase && (
                <Box sx={{ mb: 2 }}>
                  <Chip
                    size="small"
                    color={jobStatus.phase === "writing" ? "info" : "primary"}
                    label={
                      jobStatus.phase === "writing"
                        ? "Writing to DB"
                        : "Translating"
                    }
                  />
                </Box>
              )}
              <Box sx={{ mb: 2 }}>
                <Stack
                  direction="row"
                  justifyContent="space-between"
                  sx={{ mb: 0.5 }}
                >
                  <Typography variant="body2" color="text.secondary">
                    Progress: {jobStatus.processed}/{jobStatus.total}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {jobStatus.total > 0
                      ? Math.round(
                          (jobStatus.processed / jobStatus.total) * 100,
                        )
                      : 0}
                    %
                  </Typography>
                </Stack>
                <LinearProgress
                  variant="determinate"
                  value={
                    jobStatus.total > 0
                      ? Math.round(
                          (jobStatus.processed / jobStatus.total) * 100,
                        )
                      : 0
                  }
                  sx={{ height: 8, borderRadius: 4 }}
                />
              </Box>
              {jobStatus.currentEntry && (
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mb: 2, fontStyle: "italic" }}
                >
                  Current: {jobStatus.currentEntry}
                </Typography>
              )}
              <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
                <Chip
                  size="small"
                  icon={<CheckCircleIcon />}
                  label={`${jobStatus.translated} translated`}
                  color="success"
                />
                <Chip
                  size="small"
                  icon={<ErrorIcon />}
                  label={`${jobStatus.errors} errors`}
                  color="error"
                />
              </Stack>
            </Box>
          )}

          {/* Loading spinner (translation just started, no status yet) */}
          {translating && !jobStatus && (
            <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
              <CircularProgress size={32} />
            </Box>
          )}

          {/* Result display (done or failed) */}
          {!translating && jobStatus && (
            <Box sx={{ mt: 1 }}>
              {jobStatus.status === "done" ? (
                <Stack direction="row" spacing={1} alignItems="center">
                  <CheckCircleIcon color="success" />
                  <Typography variant="body2">
                    Successfully translated to {selectedLocale?.label}.
                  </Typography>
                </Stack>
              ) : (
                <Stack direction="row" spacing={1} alignItems="center">
                  <ErrorIcon color="error" />
                  <Typography variant="body2">
                    Translation failed: {jobStatus.error || "unknown error"}
                  </Typography>
                </Stack>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          {/* Pre-translation: Cancel + Translate buttons */}
          {!translating && !jobStatus && (
            <>
              <Button onClick={handleCancel}>Cancel</Button>
              <Button
                onClick={handleConfirm}
                variant="contained"
                color="primary"
              >
                Translate
              </Button>
            </>
          )}

          {/* Post-translation: Close button */}
          {!translating && jobStatus && (
            <Button onClick={handleDone} variant="contained" color="primary">
              Close
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </>
  );
}
